import {useCallback, useMemo} from 'react';
import {InfiniteData, QueryKey, useMutation, useQueryClient} from 'react-query';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CancelError} from '../../helpers/asyncCancelHelpers';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {parseApiError} from '../rules/ruleHelpers';
import {ListMutation, MutationActionLike, useListMutations} from './listMutation';
import {
  ResourceListPagination,
  ResourceTableCommonPageType,
} from './resourceListPagination/resourceListPaginationHelpers';
import {useSettingsInfiniteQuery, UseSettingsQueryOptions} from './useSettingsQuery';

export interface ResourceListSource<Resource> {
  items: ReadonlyArray<Resource>;
  moreStatus: AsyncMoreStatusesEnum;
  loadMore: Handler;
  refetch: Handler;
}

/**
 * A hook to fetch a list of resource from a search endpoint, for use in settings tables.
 *
 * This abstracts caching, pagination and cancellations.
 */
export function useResourceList<TResource, TPageType extends ResourceTableCommonPageType<TResource>>(
  queryKey: QueryKey,
  pagination: ResourceListPagination<TResource, any, TPageType>,
  options: UseSettingsQueryOptions = {},
) {
  const {showBanner} = useSettingsBanners();
  const showErrorBanner = useCallback(
    (err: Error) => {
      if (err instanceof CancelError) {
        return;
      }

      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: parseApiError(err),
      });
    },
    [showBanner],
  );

  const {data, asyncStatus, hasNextPage, fetchNextPage, refetch, isFetchingNextPage, isError} =
    useSettingsInfiniteQuery<TPageType, Error, TPageType, QueryKey>(
      queryKey,
      async ({pageParam, company, cancelToken}): Promise<TPageType> =>
        pagination.fetchApi(pageParam, company, cancelToken),
      {
        ...options,
        getNextPageParam: (lastPage) => pagination.getNextPageParam(lastPage),
        onError: showErrorBanner,
      },
    );

  const flatResources: ReadonlyArray<TResource> = useMemo(
    () => data?.pages.map((page) => page.items).flat() ?? [],
    [data?.pages],
  );

  const onLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return useMemo(
    () => ({
      items: flatResources,
      loadMore: onLoadMore,
      moreStatus: asyncStatus,
      hasError: isError,
      showErrorBanner,
      refetch,
    }),
    [flatResources, onLoadMore, asyncStatus, isError, showErrorBanner, refetch],
  );
}

export function useResourceListSource<TResource, TPageType extends ResourceTableCommonPageType<TResource>>(
  queryKey: QueryKey,
  pagination: ResourceListPagination<TResource, any, TPageType>,
) {
  const {items, loadMore, refetch, moreStatus} = useResourceList<TResource, TPageType>(queryKey, pagination);

  return useMemo(
    (): ResourceListSource<TResource> => ({items, loadMore, moreStatus, refetch}),
    [items, loadMore, moreStatus, refetch],
  );
}

export function usePersistedListMutation<
  TMutationAction extends MutationActionLike,
  TData,
  TPersistedListMutations extends ReadonlyArray<ListMutation<TData, any, any>>,
  TError = unknown,
>(
  queryKeyToUpdate: QueryKey,
  mutations: TPersistedListMutations,
  onError?: (err: TError) => unknown | Promise<unknown>,
  invalidateOnSettled?: boolean,
) {
  const queryClient = useQueryClient();

  const listMutations = useListMutations<TData, TMutationAction, TPersistedListMutations>(mutations);

  return useMutation<void, TError, TMutationAction, ReadonlyArray<[QueryKey, InfiniteData<TData>]>>(
    async (mutation) => {
      const results = await listMutations.applySideEffect(mutation);

      queryClient.setQueriesData<InfiniteData<TData> | undefined>(queryKeyToUpdate, (previous) => {
        if (!previous) {
          return previous;
        }

        return {
          ...previous,
          pages: previous.pages.map((page, index) =>
            listMutations.reconciliateCache(page, mutation, results, index),
          ),
        };
      });
    },
    {
      onMutate: async (mutation) => {
        // Cancel running queries. This prevents running query from invalidating optimistic updates.
        await queryClient.cancelQueries(queryKeyToUpdate);

        // Returns all the matching query and their data before the mutation.
        const previousState = queryClient.getQueriesData<InfiniteData<TData>>(queryKeyToUpdate);

        // Modify every query matching the query key with the optimistic mutation.
        queryClient.setQueriesData<InfiniteData<TData> | undefined>(queryKeyToUpdate, (previous) => {
          if (!previous) {
            return previous;
          }

          return {
            ...previous,
            pages: previous.pages.map((page, index) =>
              listMutations.updateOptimistically(page, mutation, index),
            ),
          };
        });

        return previousState;
      },
      onError: async (err, variables, previousState = []) => {
        // For every saved query, restore the previous state.
        for (const [key, previousData] of previousState) {
          queryClient.setQueriesData(key, previousData);
        }

        await onError?.(err);
      },
      onSettled: () => {
        // If `invalidateOnSettled` is truthy, refetch after error or success by invalidating the query key.
        if (invalidateOnSettled) {
          queryClient.invalidateQueries(queryKeyToUpdate);
        }
      },
    },
  );
}
